import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Bell, LogOut, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import { subscriptionsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import SubscriptionCard from '../components/SubscriptionCard';
import SubscriptionModal from '../components/SubscriptionModal';
import SettingsModal from '../components/SettingsModal';

const FILTERS = [
  { key: 'all', label: '📋 All' },
  { key: 'expired', label: '🔴 Overdue' },
  { key: 'soon', label: '⚠️ Due Soon' },
  { key: 'ok', label: '✅ Active' },
];

function getDaysLeft(dateStr) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const expiry = new Date(dateStr + 'T00:00:00');
  return Math.floor((expiry - today) / 86400000);
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingSub, setEditingSub] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  const loadSubs = useCallback(async () => {
    try {
      const res = await subscriptionsAPI.getAll();
      setSubs(res.data);
    } catch {
      toast.error('Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadSubs(); }, [loadSubs]);

  const handleAdd = async (data) => {
    try {
      const res = await subscriptionsAPI.create(data);
      setSubs(prev => [...prev, res.data].sort((a, b) => a.expiry_date.localeCompare(b.expiry_date)));
      setShowAddModal(false);
      toast.success('Subscription added! 🎉');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add subscription');
    }
  };

  const handleEdit = async (data) => {
    try {
      const res = await subscriptionsAPI.update(editingSub.id, data);
      setSubs(prev => prev.map(s => s.id === editingSub.id ? res.data : s)
        .sort((a, b) => a.expiry_date.localeCompare(b.expiry_date)));
      setEditingSub(null);
      toast.success('Updated! ✅');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update');
    }
  };

  const handleDelete = async (id) => {
    try {
      await subscriptionsAPI.delete(id);
      setSubs(prev => prev.filter(s => s.id !== id));
      toast.success('Deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleRenew = async (id) => {
    try {
      const res = await subscriptionsAPI.renew(id);
      setSubs(prev => prev.map(s => s.id === id ? res.data : s)
        .sort((a, b) => a.expiry_date.localeCompare(b.expiry_date)));
      toast.success('✅ Renewed! Next expiry date updated.');
    } catch {
      toast.error('Failed to renew');
    }
  };

  // Filter and search
  const filteredSubs = subs.filter(s => {
    const days = getDaysLeft(s.expiry_date);
    const matchSearch = s.name.toLowerCase().includes(search.toLowerCase());
    if (!matchSearch) return false;
    if (filter === 'expired') return days < 0;
    if (filter === 'soon') return days >= 0 && days <= 7;
    if (filter === 'ok') return days > 7;
    return true;
  });

  // Stats
  const stats = {
    total: subs.length,
    overdue: subs.filter(s => getDaysLeft(s.expiry_date) < 0).length,
    dueSoon: subs.filter(s => { const d = getDaysLeft(s.expiry_date); return d >= 0 && d <= 7; }).length,
    active: subs.filter(s => getDaysLeft(s.expiry_date) > 7).length,
  };

  return (
    <div style={styles.page}>
      {/* Sidebar */}
      <div style={styles.sidebar}>
        <div style={styles.logo}>
          <span style={styles.logoIcon}>🔔</span>
          <span style={styles.logoText}>SubRemind</span>
        </div>

        {/* User */}
        <div style={styles.userCard}>
          {user.picture && <img src={user.picture} alt="" style={styles.avatar} />}
          <div style={styles.userInfo}>
            <div style={styles.userName}>{user.name.split(' ')[0]}</div>
            <div style={styles.userEmail}>{user.email}</div>
          </div>
        </div>

        {/* Stats */}
        <div style={styles.statsGrid}>
          {[
            { label: 'Total', value: stats.total, color: '#6366f1', bg: '#f0f0ff' },
            { label: 'Overdue', value: stats.overdue, color: '#dc2626', bg: '#fff5f5' },
            { label: 'Due Soon', value: stats.dueSoon, color: '#d97706', bg: '#fffbeb' },
            { label: 'Active', value: stats.active, color: '#16a34a', bg: '#f0fdf4' },
          ].map(s => (
            <div key={s.label} style={{ ...styles.statBox, background: s.bg }}>
              <div style={{ ...styles.statValue, color: s.color }}>{s.value}</div>
              <div style={styles.statLabel}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div style={styles.filterSection}>
          <div style={styles.filterTitle}>Filter</div>
          {FILTERS.map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              style={{ ...styles.filterBtn, ...(filter === f.key ? styles.filterBtnActive : {}) }}
            >
              {f.label}
              {f.key !== 'all' && (
                <span style={{ ...styles.filterCount, background: filter === f.key ? 'rgba(255,255,255,0.3)' : '#f0f0f0' }}>
                  {f.key === 'expired' ? stats.overdue : f.key === 'soon' ? stats.dueSoon : stats.active}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Bottom actions */}
        <div style={styles.sidebarBottom}>
          <button onClick={() => setShowSettings(true)} style={styles.sideBtn}>
            <Bell size={16} /> Notifications
          </button>
          <button onClick={logout} style={{ ...styles.sideBtn, color: '#ef4444' }}>
            <LogOut size={16} /> Sign Out
          </button>
        </div>
      </div>

      {/* Main content */}
      <div style={styles.main}>
        {/* Top bar */}
        <div style={styles.topBar}>
          <div>
            <h1 style={styles.pageTitle}>Your Subscriptions</h1>
            <p style={styles.pageSubtitle}>
              {stats.overdue > 0
                ? `🚨 ${stats.overdue} overdue — action needed!`
                : stats.dueSoon > 0
                  ? `⚠️ ${stats.dueSoon} expiring within 7 days`
                  : '✅ All subscriptions are up to date'}
            </p>
          </div>
          <button onClick={() => setShowAddModal(true)} style={styles.addBtn}>
            <Plus size={20} />
            Add Subscription
          </button>
        </div>

        {/* Search */}
        <div style={styles.searchWrap}>
          <Search size={16} color="#aaa" style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)' }} />
          <input
            style={styles.searchInput}
            placeholder="Search subscriptions..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* List */}
        {loading ? (
          <div style={styles.emptyState}>
            <div style={styles.spinner} />
            <p style={{ color: '#888' }}>Loading your subscriptions...</p>
          </div>
        ) : filteredSubs.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>
              {subs.length === 0 ? '📭' : '🔍'}
            </div>
            <h3 style={styles.emptyTitle}>
              {subs.length === 0 ? 'No subscriptions yet' : 'No results found'}
            </h3>
            <p style={styles.emptyDesc}>
              {subs.length === 0
                ? 'Add your first subscription to start tracking renewals and getting reminders.'
                : 'Try adjusting your search or filter.'}
            </p>
            {subs.length === 0 && (
              <button onClick={() => setShowAddModal(true)} style={styles.emptyAddBtn}>
                <Plus size={18} /> Add Your First Subscription
              </button>
            )}
          </div>
        ) : (
          <div style={styles.grid}>
            {filteredSubs.map(sub => (
              <SubscriptionCard
                key={sub.id}
                sub={sub}
                onEdit={setEditingSub}
                onDelete={handleDelete}
                onRenew={handleRenew}
              />
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      {showAddModal && (
        <SubscriptionModal onSave={handleAdd} onClose={() => setShowAddModal(false)} />
      )}
      {editingSub && (
        <SubscriptionModal subscription={editingSub} onSave={handleEdit} onClose={() => setEditingSub(null)} />
      )}
      {showSettings && (
        <SettingsModal onClose={() => setShowSettings(false)} />
      )}
    </div>
  );
}

const styles = {
  page: {
    display: 'flex', minHeight: '100vh',
    background: '#f4f6ff',
    fontFamily: '"Segoe UI", system-ui, sans-serif',
  },
  sidebar: {
    width: '260px', flexShrink: 0,
    background: 'linear-gradient(180deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
    display: 'flex', flexDirection: 'column',
    padding: '24px 16px',
    position: 'sticky', top: 0, height: '100vh',
  },
  logo: {
    display: 'flex', alignItems: 'center', gap: '10px',
    padding: '0 8px 24px',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
  },
  logoIcon: { fontSize: '28px' },
  logoText: {
    fontSize: '22px', fontWeight: '800', color: 'white',
    fontFamily: '"Georgia", serif',
  },
  userCard: {
    display: 'flex', alignItems: 'center', gap: '10px',
    padding: '16px 8px',
    background: 'rgba(255,255,255,0.08)',
    borderRadius: '12px', margin: '16px 0',
  },
  avatar: { width: '40px', height: '40px', borderRadius: '50%' },
  userInfo: {},
  userName: { color: 'white', fontWeight: '600', fontSize: '14px' },
  userEmail: { color: 'rgba(255,255,255,0.5)', fontSize: '11px', marginTop: '2px' },
  statsGrid: {
    display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '20px',
  },
  statBox: {
    borderRadius: '10px', padding: '10px', textAlign: 'center',
  },
  statValue: { fontSize: '22px', fontWeight: '800', lineHeight: '1' },
  statLabel: { fontSize: '11px', color: '#888', marginTop: '2px', fontWeight: '500' },
  filterSection: { marginBottom: 'auto' },
  filterTitle: {
    color: 'rgba(255,255,255,0.4)', fontSize: '11px',
    fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px',
    padding: '0 8px', marginBottom: '8px',
  },
  filterBtn: {
    width: '100%', padding: '10px 12px',
    border: 'none', borderRadius: '10px', cursor: 'pointer',
    background: 'transparent', color: 'rgba(255,255,255,0.6)',
    fontSize: '14px', fontWeight: '500', textAlign: 'left',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    marginBottom: '4px', transition: 'all 0.15s',
  },
  filterBtnActive: {
    background: 'rgba(99,102,241,0.3)',
    color: 'white',
    fontWeight: '700',
  },
  filterCount: {
    fontSize: '11px', padding: '2px 8px',
    borderRadius: '10px', fontWeight: '700',
  },
  sidebarBottom: {
    borderTop: '1px solid rgba(255,255,255,0.1)',
    paddingTop: '16px', marginTop: '16px',
    display: 'flex', flexDirection: 'column', gap: '4px',
  },
 sideBtn: {
    display: 'flex', alignItems: 'center', gap: '8px',
    padding: '10px 12px', borderRadius: '10px',
    border: 'none', background: 'rgba(255,255,255,0.1)',
    color: 'white', fontSize: '14px',
    cursor: 'pointer', width: '100%', textAlign: 'left',
    marginBottom: '6px',
  },
  main: { flex: 1, padding: '32px', overflowY: 'auto' },
  topBar: {
    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
    marginBottom: '24px',
  },
  pageTitle: {
    margin: '0 0 4px', fontSize: '28px', fontWeight: '800', color: '#1a1a2e',
  },
  pageSubtitle: { margin: 0, color: '#888', fontSize: '14px' },
  addBtn: {
    display: 'flex', alignItems: 'center', gap: '8px',
    background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
    color: 'white', border: 'none',
    padding: '12px 20px', borderRadius: '12px',
    fontSize: '15px', fontWeight: '700', cursor: 'pointer',
    boxShadow: '0 4px 16px rgba(99,102,241,0.4)',
    whiteSpace: 'nowrap',
  },
  searchWrap: {
    position: 'relative', marginBottom: '20px',
  },
  searchInput: {
    width: '100%', padding: '12px 16px 12px 42px',
    border: '2px solid #e8e8e8', borderRadius: '12px',
    fontSize: '15px', outline: 'none', boxSizing: 'border-box',
    background: 'white', color: '#1a1a2e',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))',
    gap: '16px',
  },
  emptyState: {
    textAlign: 'center', padding: '80px 40px',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px',
  },
  emptyIcon: { fontSize: '64px' },
  emptyTitle: { margin: 0, fontSize: '22px', fontWeight: '700', color: '#1a1a2e' },
  emptyDesc: { margin: 0, color: '#888', fontSize: '15px', maxWidth: '360px', lineHeight: '1.6' },
  emptyAddBtn: {
    display: 'flex', alignItems: 'center', gap: '8px',
    background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
    color: 'white', border: 'none',
    padding: '14px 24px', borderRadius: '12px',
    fontSize: '15px', fontWeight: '700', cursor: 'pointer',
    marginTop: '8px',
  },
  spinner: {
    width: '40px', height: '40px',
    border: '3px solid #e8e8e8',
    borderTopColor: '#6366f1',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
};
